import agent, { type LaunchableFromList } from "server";
import { api, type AxiosRes, type BaseRes } from "server/config";
import type { LifeCycleScriptAttr } from "components/Environment/shared/BuildTypes";

export type LaunchableVisibility = "public" | "authenticated";

export class LaunchableRequestError extends Error {
  readonly notFound: boolean;
  readonly accessDenied: boolean;

  constructor(message: string) {
    super(message);
    this.name = "LaunchableRequestError";
    this.notFound = message.includes("environment not found");
    this.accessDenied = message.includes("Access Denied:");
  }
}

export async function getLaunchable(
  launchableId: string,
  visibility: LaunchableVisibility,
): Promise<LaunchableFromList> {
  const response =
    visibility === "public"
      ? await agent.Launchables.getPublicLaunchable(launchableId)
      : await agent.Launchables.getLaunchableWithAuth(launchableId);

  if (!response.success || !response.data) {
    throw new LaunchableRequestError(
      response.message || "Error fetching launchable data",
    );
  }

  return response.data;
}

type LaunchableLifecycleScriptResponse = {
  data?: {
    lifecycle_script: string;
    attrs: LifeCycleScriptAttr;
  };
} & AxiosRes &
  BaseRes;

export async function getLaunchableLifecycleScript(
  launchableId: string,
  lifecycleScriptId: string,
  signal?: AbortSignal,
): Promise<string> {
  const response = (await api.get("/api/launchable/lifecycle-script", {
    params: {
      envId: launchableId,
      scriptId: lifecycleScriptId,
    },
    signal,
  })) as unknown as LaunchableLifecycleScriptResponse;

  if (!response.success) {
    throw new Error(response.message || "Error fetching lifecycle script");
  }

  return response.data?.attrs?.script ?? "";
}
